import { CountryIsoCode } from 'schools-domain-backend-utils'
import { ApiStudentRequest, request } from '../utils'
import { useAuthQuery } from './auth'

export enum DocumentProcessStatus {
  NOT_UPLOADED = 'NOT_UPLOADED', // Before S3 presigned url is used
  DOCUMENT_TYPE_VALIDATION_FAILED = 'DOCUMENT_TYPE_VALIDATION_FAILED',
  DOCUMENT_TYPE_VALIDATION_COMPLETED = 'DOCUMENT_TYPE_VALIDATION_COMPLETED',
  DOCUMENT_DATA_EXTRACTION_FAILED = 'DOCUMENT_DATA_EXTRACTION_FAILED',
  DOCUMENT_DATA_EXTRACTION_COMPLETED = 'DOCUMENT_DATA_EXTRACTION_COMPLETED',
}

export enum ConfidenceLevel {
  HIGH = 'high',
  LOW = 'low',
  MISMATCH = 'mismatch',
  NONE = 'none',
}

type ValueWithConfidence<T> = {
  value: T
  confidence: ConfidenceLevel
}

type PassportExtraction = {
  firstName?: ValueWithConfidence<string>
  familyName?: ValueWithConfidence<string>
  middleName?: ValueWithConfidence<string>
  passportNumber?: ValueWithConfidence<string>
  expiryDate?: ValueWithConfidence<string>
  nationality?: ValueWithConfidence<CountryIsoCode>
  birthDate?: ValueWithConfidence<string>
  sex?: ValueWithConfidence<string>
  address?: {
    address?: string
    city?: string
    province?: string
    postalCode?: string
    country?: string
    confidence?: ConfidenceLevel
  }
}

export type Passport = {
  id: string
  type: 'passport'
  attributes: {
    passportNumber?: string
    expiryDate?: string
    nationality?: CountryIsoCode
    fileName: string
    documentProcessStatus?: DocumentProcessStatus
    uploadedAt: string
    processingResults?: {
      documentTypeConfidence?: ConfidenceLevel
      extractedData?: PassportExtraction
    }
  }
}

export type RawGetPassportResponse = {
  data: Passport
}

type GetPassportProps = {
  id: string | undefined
  refetchCondition?: (data: Passport | undefined) => boolean
}

export const isPassportProcessingComplete = (passport?: Passport) => {
  const status = passport?.attributes?.documentProcessStatus
  return (
    status &&
    [
      DocumentProcessStatus.DOCUMENT_DATA_EXTRACTION_COMPLETED,
      DocumentProcessStatus.DOCUMENT_DATA_EXTRACTION_FAILED,
      DocumentProcessStatus.DOCUMENT_TYPE_VALIDATION_FAILED,
    ].includes(status)
  )
}

export function useGetPassport(props: GetPassportProps) {
  const { isLoading, isFetching, data } = useAuthQuery(
    ['passport', props.id],
    async () => await GetPassport(props.id),
    {
      enabled: !!props.id,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchInterval: ({ state }) => {
        return isPassportProcessingComplete(state.data?.data) ? false : 5000
      },
    },
  )

  return {
    isLoadingPassport: isLoading,
    isFetchingPassport: isFetching,
    passport: data?.data,
  }
}

export async function GetPassport(id?: string) {
  return request<RawGetPassportResponse>(
    new ApiStudentRequest(`/documents/passports/${id}`, {
      method: 'GET',
    }),
    {
      isExpectedResponse: (res): res is RawGetPassportResponse => res,
    },
  )
}
